import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { EmptyState } from "~/components/empty-state";
import { ChevronLeft, ChevronRight, FileSearch } from "lucide-react";
import { cn } from "~/lib/utils";

interface ColumnMeta {
  cellClassName?: string;
  headClassName?: string;
}

interface DataTableProps<T> {
  data: T[] | undefined;
  columns: ColumnDef<T, unknown>[];
  total?: number;
  limit: number;
  offset: number;
  onPageChange: (offset: number) => void;
  loading?: boolean;
  emptyTitle?: string;
  emptyBody?: string;

  emptyMessage?: string;

  caption?: string;
  className?: string;
  getRowId?: (row: T) => string;
  onRowClick?: (row: T) => void;
  selectedRowId?: string;
}

export function DataTable<T>({
  data,
  columns,
  total,
  limit,
  offset,
  onPageChange,
  loading,
  emptyTitle,
  emptyBody,
  emptyMessage,
  caption,
  className,
  getRowId,
  onRowClick,
  selectedRowId,
}: DataTableProps<T>) {
  const resolvedEmptyTitle = emptyTitle ?? emptyMessage ?? "No results";
  const table = useReactTable({
    data: data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId,
  });

  const colCount = columns.length;
  const showingFrom = data && data.length > 0 ? offset + 1 : 0;
  const showingTo = offset + (data?.length ?? 0);
  const hasPrev = offset > 0;
  const hasNext = total !== undefined ? offset + limit < total : (data?.length ?? 0) === limit;
  const hasContent = loading || (data && data.length > 0);

  return (
    <div className={cn("space-y-2.5", className)}>
      <div className="border-hairline overflow-hidden rounded-lg border bg-card">
        {hasContent ? (
          <Table>
            {caption && <caption className="sr-only">{caption}</caption>}
            <TableHeader>
              {table.getHeaderGroups().map((hg) => (
                <TableRow key={hg.id} className="hover:bg-transparent">
                  {hg.headers.map((h) => {
                    const meta = h.column.columnDef.meta as ColumnMeta | undefined;
                    return (
                      <TableHead key={h.id} className={meta?.headClassName}>
                        {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {loading
                ? Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={i} className="hover:bg-transparent">
                      {Array.from({ length: colCount }).map((__, j) => (
                        <TableCell key={j}>
                          <Skeleton className="h-3 w-full max-w-[140px]" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : table.getRowModel().rows.map((row) => {
                    const clickable = !!onRowClick;
                    const activate = () => onRowClick?.(row.original);
                    return (
                      <TableRow
                        key={row.id}
                        onClick={
                          clickable
                            ? (e) => {
                                const target = e.target as HTMLElement;
                                if (target.closest("a,button,input,[role=button]")) {
                                  return;
                                }
                                activate();
                              }
                            : undefined
                        }
                        onKeyDown={
                          clickable
                            ? (e) => {
                                if (e.target !== e.currentTarget) return;
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  activate();
                                }
                              }
                            : undefined
                        }
                        tabIndex={clickable ? 0 : undefined}
                        data-selected={row.id === selectedRowId ? "" : undefined}
                        className={cn(
                          clickable &&
                            "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
                          row.id === selectedRowId && "bg-accent/60",
                        )}
                      >
                        {row.getVisibleCells().map((cell) => {
                          const meta = cell.column.columnDef.meta as ColumnMeta | undefined;
                          return (
                            <TableCell key={cell.id} className={meta?.cellClassName}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
            </TableBody>
          </Table>
        ) : (
          <EmptyState
            icon={<FileSearch className="size-4" strokeWidth={1.75} />}
            title={resolvedEmptyTitle}
            body={emptyBody}
          />
        )}
      </div>

      {hasContent && (
        <div className="text-text-faint flex items-center justify-between text-[12px]">
          <span className="tabular">
            {total !== undefined
              ? `Showing ${showingFrom}–${showingTo} of ${total}`
              : `Showing ${showingFrom}–${showingTo}`}
          </span>
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              disabled={!hasPrev || loading}
              onClick={() => onPageChange(Math.max(0, offset - limit))}
            >
              <ChevronLeft className="size-3.5" /> Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!hasNext || loading}
              onClick={() => onPageChange(offset + limit)}
            >
              Next <ChevronRight className="size-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
